import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import styled from 'styled-components';
import {Col, Container, Form, InputGroup, Row} from 'react-bootstrap';
import {useSearchParams} from 'react-router-dom';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {useAppDispatch} from 'store/customer';
import {setIsRoomPlannerEditing} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {setProductUpdated} from 'components/customer/Product/store/productSlice';
import {useDebouncedCallback} from 'use-debounce';
import {CBCButton, genericMessageHandler, Loader} from 'shared/helpers';
import {FormControl} from 'shared/components/Forms/FormControl';
import {
    addPlaceholderQuantityGroup,
    applyPlaceholderDimensions,
    applyPlaceholderGroupQuantity,
    getPlaceholderDimensionsMm,
    PlaceholderConfig,
    syncPlaceholderCabinetDimensions,
} from 'components/customer/RoomPlanner/helpers/placeholder/loadPlaceholderGLB';
import {toPlaceholderConfig} from 'components/customer/RoomPlanner/helpers/placeholder/catalog';
import {
    PlaceholderHistorySnapshot,
    recordPlaceholderAdd,
    recordPlaceholderUpdate,
    revertPlaceholderEditPreview,
    snapshotPlaceholder,
} from 'components/customer/RoomPlanner/helpers/history/placeholderHistory';
import {autoSave} from 'components/customer/RoomPlanner/lib/useAutoSave';
import {
    Event,
    genericPubSub,
} from 'components/customer/RoomPlanner/helper/GenericPubSub';
import {FormGroup} from 'shared/components/Forms/FormGroup';
import {Number as QuantityNumber} from 'shared/components/Forms/Number';
import {useMenuContext} from 'components/customer/RoomPlanner/components/Menu/MenuContext';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import CameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {useGetPlaceholderFacesQuery} from 'components/customer/RoomPlanner/store/placeholderFaceApi';
import {useNotificationContext} from 'contexts/NotificationContext';
import {
    PLACEHOLDER_EDIT_PARAM,
    PLACEHOLDER_FACE_PARAM,
    PLACEHOLDER_NAME_PARAM,
} from 'components/customer/RoomPlanner/components/FloatingMenu/Actions/PlaceholderEdit';
import {PLACEHOLDER_NOT_IN_ORDER_LABEL} from 'components/customer/RoomPlanner/helpers/placeholder/copy';
import {
    clampPlaceholderQuantity,
    findPlaceholderGroupOf,
    MAX_PLACEHOLDER_QUANTITY,
} from 'components/customer/RoomPlanner/helpers/placeholder/quantity';
import {dimensionLimitError} from 'components/customer/RoomPlanner/helpers/placeholder/dimensionLimits';

/* eslint-disable react/no-multi-comp -- colocated field helpers for this editor */

type ActualDimensions = {
    width: number;
    height: number;
    depth: number;
};

type PlaceholderValues = Record<string, unknown> & {
    actualDimensions?: ActualDimensions;
    quantity?: number;
};

type EditSnapshot = {
    dimensions: ActualDimensions;
    quantity: number;
};

const getPlaceholderValues = (group: CabinetMesh): PlaceholderValues => {
    return (group.userData?.values as unknown as PlaceholderValues) ?? {};
};

const RequiredMark = () => <RequiredAsterisk>*</RequiredAsterisk>;

const DimensionField = ({
    label,
    name,
    value,
    onCommit,
    readOnly = false,
    error,
}: {
    label: string;
    name: string;
    value: number;
    onCommit: (name: string, value: string | number) => void;
    readOnly?: boolean;
    error?: string | null;
}) => {
    return (
        <FormGroup controlId={`placeholder-${name}`}>
            <Form.Label column xs={6}>
                {label}:{'\u00A0'}
                {readOnly ? null : <RequiredMark />}
            </Form.Label>
            <Col xs={6}>
                <InputGroup className="cbc-size" style={{width: '150px'}}>
                    <FormControl
                        hasFormik={false}
                        type="number"
                        name={name}
                        value={value}
                        selectHandler={onCommit}
                        disabled={readOnly}
                        isInvalid={Boolean(error)}
                        preventMinInput={false}
                        style={{fontSize: '0.95em', padding: '5px'}}
                    />
                    <SizeSuffix>mm</SizeSuffix>
                </InputGroup>
            </Col>
        </FormGroup>
    );
};

/**
 * Specs form shown in the appliance panel for placeholders.
 * Header, breadcrumbs, and close stay on the appliance panel.
 * @return {JSX.Element | null}
 */
const PlaceholderEditor: React.FC = () => {
    const {selectedCabinetGroup, cabinetGroupListRef, placeholderListRef} =
        useRoomPlannerContext();
    const dispatch = useAppDispatch();
    const {closeProductEditorMenu} = useMenuContext();
    const {notify, addMessages} = useNotificationContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const {data: placeholderFaces = [], isLoading: facesLoading} =
        useGetPlaceholderFacesQuery();

    const placeholderFaceId = searchParams.get(PLACEHOLDER_FACE_PARAM);
    const placeholderEdit = searchParams.get(PLACEHOLDER_EDIT_PARAM);
    const isAddMode = Boolean(placeholderFaceId) && !placeholderEdit;
    const isPlaceholder = Boolean(
        selectedCabinetGroup?.userData?.isPlaceholder
    );

    const catalogItem = useMemo(() => {
        if (!placeholderFaceId) return null;
        const face = placeholderFaces.find(
            (item) => String(item.id) === String(placeholderFaceId)
        );
        if (!face) return null;
        const config = toPlaceholderConfig(face);
        if (!config) return null;
        return {face, config};
    }, [placeholderFaceId, placeholderFaces]);

    const activeFace = useMemo(() => {
        if (catalogItem) return catalogItem.face;

        const faceId = (
            selectedCabinetGroup?.userData as
                {placeholderFaceId?: number | null} | undefined
        )?.placeholderFaceId;
        if (faceId == null) return null;

        return (
            placeholderFaces.find(
                (item) => String(item.id) === String(faceId)
            ) ?? null
        );
    }, [catalogItem, placeholderFaces, selectedCabinetGroup]);

    // Default to locked so a face that has not loaded yet cannot be resized.
    const canResize = Boolean(activeFace?.is_resizable);

    const [dimensions, setDimensions] = useState({
        width: 0,
        height: 0,
        depth: 0,
    });
    const dimensionsRef = useRef(dimensions);
    dimensionsRef.current = dimensions;
    const [quantity, setQuantity] = useState(1);
    const [saving, setSaving] = useState(false);
    // Values the editor opened with. Compared against the live fields so the
    // discard warning only appears once something actually differs.
    const [baseline, setBaseline] = useState<EditSnapshot | null>(null);

    const originalSnapshotRef = useRef<EditSnapshot | null>(null);
    // Copies as they were when the editor opened. Live preview mutates the
    // meshes, so the undo "before" has to be taken here, not at save time.
    const historyBeforeRef = useRef<PlaceholderHistorySnapshot[]>([]);
    const editSessionIdRef = useRef<string | null>(null);
    const groupRef = useRef<CabinetMesh | null>(null);
    const savedRef = useRef(false);

    const applyPreviewDimensions = useCallback(
        (
            group: CabinetMesh,
            newDimensions: ActualDimensions,
            nextQuantity = quantity
        ) => {
            const members = findPlaceholderGroupOf(
                placeholderListRef?.current,
                group
            );
            const targets = members.length > 0 ? members : [group];
            targets.forEach((item) => {
                applyPlaceholderDimensions(item, newDimensions);
                syncPlaceholderCabinetDimensions(item, newDimensions, {
                    quantity: nextQuantity,
                });
            });
        },
        [quantity, placeholderListRef]
    );

    const previewDimensions = useDebouncedCallback(
        (newDimensions: ActualDimensions) => {
            const group = groupRef.current;
            if (!group) return;
            applyPreviewDimensions(group, newDimensions);
        },
        150
    );

    const initializeEditSession = useCallback(() => {
        if (!selectedCabinetGroup || !isPlaceholder || isAddMode) return;

        const sessionId = String(selectedCabinetGroup.userData.id ?? '');
        if (placeholderEdit && sessionId !== String(placeholderEdit)) {
            return;
        }
        if (editSessionIdRef.current === sessionId) return;

        const currentValues = getPlaceholderValues(selectedCabinetGroup);
        const initialDimensions =
            getPlaceholderDimensionsMm(selectedCabinetGroup);
        const groupMembers = findPlaceholderGroupOf(
            placeholderListRef?.current,
            selectedCabinetGroup
        );
        const initialQuantity = clampPlaceholderQuantity(
            groupMembers.length || Number(currentValues.quantity) || 1
        );

        syncPlaceholderCabinetDimensions(
            selectedCabinetGroup,
            initialDimensions,
            {quantity: initialQuantity}
        );

        groupRef.current = selectedCabinetGroup;
        savedRef.current = false;
        const historyMembers =
            groupMembers.length > 0 ? groupMembers : [selectedCabinetGroup];
        historyBeforeRef.current = historyMembers
            .map((item) => snapshotPlaceholder(item))
            .filter((item): item is PlaceholderHistorySnapshot => item != null);
        originalSnapshotRef.current = {
            dimensions: {...initialDimensions},
            quantity: initialQuantity,
        };
        editSessionIdRef.current = sessionId;
        setDimensions(initialDimensions);
        setQuantity(initialQuantity);
        setBaseline({
            dimensions: {...initialDimensions},
            quantity: initialQuantity,
        });
    }, [
        selectedCabinetGroup,
        isPlaceholder,
        isAddMode,
        placeholderEdit,
        placeholderListRef,
    ]);

    useEffect(() => {
        initializeEditSession();
    }, [initializeEditSession]);

    useEffect(() => {
        if (!isAddMode || !catalogItem) return;
        const catalogDefaults = {
            height: Number(catalogItem.face.height) || 0,
            width: Number(catalogItem.face.width) || 0,
            depth: Number(catalogItem.face.depth) || 0,
        };
        setDimensions(catalogDefaults);
        setQuantity(1);
        setBaseline({dimensions: catalogDefaults, quantity: 1});
        savedRef.current = false;
        groupRef.current = null;
        editSessionIdRef.current = null;
        originalSnapshotRef.current = null;
    }, [isAddMode, catalogItem]);

    const hasUnsavedChanges = useMemo(() => {
        if (!baseline) return false;

        return (
            dimensions.width !== baseline.dimensions.width ||
            dimensions.height !== baseline.dimensions.height ||
            dimensions.depth !== baseline.dimensions.depth ||
            quantity !== baseline.quantity
        );
    }, [baseline, dimensions, quantity]);

    /*
     * Placeholders share the product editor's dirty flag so closing, or
     * clicking another item in the room, prompts exactly when a cabinet would.
     */
    useEffect(() => {
        dispatch(setProductUpdated(hasUnsavedChanges));
    }, [dispatch, hasUnsavedChanges]);

    useEffect(
        () => () => {
            dispatch(setProductUpdated(false));
        },
        [dispatch]
    );

    // Discard: the panel remounts this editor per placeholder, so unmount
    // puts the one being left back before the next session snapshots.
    useEffect(() => {
        return () => {
            previewDimensions.cancel();
            if (savedRef.current) return;
            revertPlaceholderEditPreview(
                historyBeforeRef.current,
                placeholderListRef?.current ?? [],
                originalSnapshotRef.current,
                groupRef.current
            );
        };
    }, [previewDimensions, placeholderListRef]);

    const limitErrorFor = useCallback(
        (
            next: ActualDimensions,
            field: keyof ActualDimensions
        ): string | null => {
            if (!canResize || !activeFace || !baseline) return null;

            if (field === 'width') {
                return dimensionLimitError(
                    'Width',
                    next.width,
                    activeFace.min_width,
                    activeFace.max_width
                );
            }
            if (field === 'height') {
                return dimensionLimitError(
                    'Height',
                    next.height,
                    activeFace.min_height,
                    activeFace.max_height
                );
            }
            return dimensionLimitError(
                'Depth',
                next.depth,
                activeFace.min_depth,
                activeFace.max_depth
            );
        },
        [activeFace, baseline, canResize]
    );

    const hasDimensionError = useCallback(
        (next: ActualDimensions) =>
            (['width', 'height', 'depth'] as const).some((field) =>
                Boolean(limitErrorFor(next, field))
            ),
        [limitErrorFor]
    );

    const handleDimensionCommit = useCallback(
        (field: 'width' | 'height' | 'depth') =>
            (_name: string, nextValue: string | number) => {
                if (!canResize) return;

                const parsed = parseFloat(String(nextValue));
                const value = Number.isNaN(parsed) ? 0 : parsed;
                const next = {
                    ...dimensionsRef.current,
                    [field]: value,
                };
                dimensionsRef.current = next;
                setDimensions(next);

                if (isAddMode) return;
                if (!hasDimensionError(next)) {
                    previewDimensions(next);
                } else {
                    previewDimensions.cancel();
                }
            },
        [canResize, hasDimensionError, isAddMode, previewDimensions]
    );

    const dimensionErrorMessages = useMemo(() => {
        return (['height', 'width', 'depth'] as const).flatMap((field) => {
            const message = limitErrorFor(dimensions, field);
            return message ? [{message, field}] : [];
        });
    }, [dimensions, limitErrorFor]);

    // Same field error a cabinet size input shows after you leave the box.
    useEffect(() => {
        addMessages?.({
            type: 'error',
            messages: dimensionErrorMessages.map(({message, field}) => ({
                message,
                fields: [field],
            })),
        });
    }, [addMessages, dimensionErrorMessages]);

    useEffect(
        () => () => {
            addMessages?.({type: 'error', messages: []});
        },
        [addMessages]
    );

    const handleQuantitySelect = useCallback(
        (_name: string, nextValue: string | number) => {
            const value = clampPlaceholderQuantity(
                parseInt(String(nextValue), 10)
            );
            setQuantity(value);
        },
        []
    );

    const returnToCatalog = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.delete(PLACEHOLDER_FACE_PARAM);
        next.delete(PLACEHOLDER_NAME_PARAM);
        next.delete(PLACEHOLDER_EDIT_PARAM);
        setSearchParams(next);
    }, [searchParams, setSearchParams]);

    const handleCancel = useCallback(() => {
        savedRef.current = false;
        if (isAddMode) {
            returnToCatalog();
            return;
        }
        if (closeProductEditorMenu) {
            closeProductEditorMenu();
            return;
        }
        dispatch(setIsRoomPlannerEditing(false));
    }, [isAddMode, returnToCatalog, closeProductEditorMenu, dispatch]);

    const configWithDimensions = useCallback(
        (config: PlaceholderConfig): PlaceholderConfig => {
            const hasSize =
                dimensions.height > 0 &&
                dimensions.width > 0 &&
                dimensions.depth > 0;
            if (!hasSize || hasDimensionError(dimensions)) return config;
            return {
                ...config,
                targetHeightMm: dimensions.height,
                targetWidthMm: dimensions.width,
                targetDepthMm: dimensions.depth,
            };
        },
        [dimensions, hasDimensionError]
    );

    const handleSaveProduct = useCallback(async () => {
        if (!catalogItem || saving || hasDimensionError(dimensions)) return;
        const scene = CameraSetup?.scene;
        if (!scene || !placeholderListRef || !cabinetGroupListRef) {
            genericMessageHandler(notify, {
                message: `There was an error adding ${catalogItem.face.name}`,
            });
            return;
        }

        const count = clampPlaceholderQuantity(quantity);
        setSaving(true);
        try {
            const config = configWithDimensions(catalogItem.config);
            const copies = await addPlaceholderQuantityGroup(
                config,
                scene,
                placeholderListRef,
                cabinetGroupListRef,
                count
            );
            recordPlaceholderAdd(
                copies,
                placeholderListRef,
                cabinetGroupListRef,
                'Add'
            );
            savedRef.current = true;
            dispatch(setProductUpdated(false));
            genericMessageHandler(
                notify,
                {
                    message:
                        count > 1
                            ? `${count}× ${catalogItem.face.name} added`
                            : `${catalogItem.face.name} added`,
                },
                'success'
            );
            returnToCatalog();
        } catch (error) {
            const message =
                error instanceof Error && error.message
                    ? error.message
                    : `There was an error adding ${catalogItem.face.name}`;
            genericMessageHandler(notify, {message});
        } finally {
            setSaving(false);
        }
    }, [
        catalogItem,
        saving,
        quantity,
        configWithDimensions,
        placeholderListRef,
        cabinetGroupListRef,
        notify,
        returnToCatalog,
        dispatch,
        dimensions,
        hasDimensionError,
    ]);

    const handleUpdate = useCallback(async (): Promise<boolean> => {
        const group = groupRef.current;
        if (!group) {
            handleCancel();
            return false;
        }

        if (hasDimensionError(dimensions)) return false;

        previewDimensions.cancel();
        const before = historyBeforeRef.current;
        applyPreviewDimensions(group, dimensions, quantity);
        const scene = CameraSetup?.scene;
        const recordUpdate = (members: CabinetMesh[]) => {
            if (!placeholderListRef || !cabinetGroupListRef) return;
            recordPlaceholderUpdate(
                before,
                members,
                placeholderListRef,
                cabinetGroupListRef
            );
        };
        if (scene && placeholderListRef && cabinetGroupListRef) {
            const members = await applyPlaceholderGroupQuantity(
                group,
                quantity,
                scene,
                placeholderListRef,
                cabinetGroupListRef
            );
            recordUpdate(members);
            autoSave(group, true);
        } else {
            recordUpdate(
                findPlaceholderGroupOf(placeholderListRef?.current, group)
            );
            autoSave(group, true);
        }
        savedRef.current = true;
        dispatch(setProductUpdated(false));
        if (closeProductEditorMenu) {
            closeProductEditorMenu();
            return true;
        }
        dispatch(setIsRoomPlannerEditing(false));
        return true;
    }, [
        hasDimensionError,
        dimensions,
        quantity,
        applyPreviewDimensions,
        previewDimensions,
        handleCancel,
        closeProductEditorMenu,
        dispatch,
        placeholderListRef,
        cabinetGroupListRef,
    ]);

    // The unsaved-changes dialog Save button publishes this. Without a
    // subscriber the dialog closed and the size edit was dropped.
    useEffect(() => {
        const submitFromDialog = () => handleUpdate();

        genericPubSub.subscribe(
            Event.ROOM_PLANNER_SUBMIT_PRODUCT_EDITOR,
            submitFromDialog
        );

        return () => {
            genericPubSub.unsubscribe(
                Event.ROOM_PLANNER_SUBMIT_PRODUCT_EDITOR,
                submitFromDialog
            );
        };
    }, [handleUpdate]);

    const preventSubmit = useCallback((event: React.FormEvent) => {
        event.preventDefault();
    }, []);

    const handleConfirmClick = useCallback(() => {
        void (isAddMode ? handleSaveProduct() : handleUpdate());
    }, [isAddMode, handleSaveProduct, handleUpdate]);

    if (isAddMode && facesLoading) {
        return <Loader loader={true} />;
    }

    if (isAddMode && placeholderFaceId && !catalogItem) {
        return null;
    }

    if (!isAddMode && !isPlaceholder && !placeholderEdit) {
        return null;
    }

    if (!isAddMode && !isPlaceholder && !groupRef.current) {
        return null;
    }

    return (
        <EditorShell>
            <Form className="cbc-form" onSubmit={preventSubmit}>
                <SpecsTitle>Specs</SpecsTitle>
                <OrderNotice>{PLACEHOLDER_NOT_IN_ORDER_LABEL}</OrderNotice>
                <FormGroup controlId="placeholder-quantity">
                    <Form.Label column xs={6}>
                        Quantity:{'\u00A0'}
                        <RequiredMark />
                    </Form.Label>
                    <Col xs={6}>
                        <QuantityNumber
                            name="quantity"
                            value={quantity}
                            selectHandler={handleQuantitySelect}
                            hasFormik={false}
                            min={1}
                            max={MAX_PLACEHOLDER_QUANTITY}
                            step={1}
                            in_sizes_tab={true}
                            fieldIndex={0}
                        />
                    </Col>
                </FormGroup>
                <FieldsetTitle className="fieldset-title">
                    Dimensions
                </FieldsetTitle>
                {canResize ? null : (
                    <LockedSizeNote>
                        This appliance is supplied at a fixed size and cannot be
                        resized.
                    </LockedSizeNote>
                )}
                <DimensionField
                    label="Height"
                    name="height"
                    value={dimensions.height}
                    onCommit={handleDimensionCommit('height')}
                    readOnly={!canResize}
                    error={limitErrorFor(dimensions, 'height')}
                />
                <DimensionField
                    label="Width"
                    name="width"
                    value={dimensions.width}
                    onCommit={handleDimensionCommit('width')}
                    readOnly={!canResize}
                    error={limitErrorFor(dimensions, 'width')}
                />
                <DimensionField
                    label="Depth"
                    name="depth"
                    value={dimensions.depth}
                    onCommit={handleDimensionCommit('depth')}
                    readOnly={!canResize}
                    error={limitErrorFor(dimensions, 'depth')}
                />
                <div className="nav-and-control">
                    <Container>
                        <Row
                            className="control-buttons"
                            style={{padding: '15px 0'}}>
                            <Col xs={6}>
                                <FooterButton
                                    type="button"
                                    onClick={handleCancel}
                                    iconName="Button-Cancel.svg"
                                    className="job-button button-light"
                                    style={{color: '#8a8080'}}>
                                    Cancel
                                </FooterButton>
                            </Col>
                            <Col xs={6}>
                                <FooterButton
                                    type="button"
                                    onClick={handleConfirmClick}
                                    iconName="Button-Tick.svg"
                                    className="job-button button-blue"
                                    disabled={saving}>
                                    {isAddMode ? 'Save Product' : 'Update'}
                                </FooterButton>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </Form>
        </EditorShell>
    );
};

export default PlaceholderEditor;

const EditorShell = styled.div`
    width: 100%;
    background: #fff;
    padding-bottom: 110px;
`;

const SpecsTitle = styled.h1`
    margin: 15px 0 8px !important;
    padding: 2px 10px !important;
`;

const OrderNotice = styled.div`
    margin: 0 0 12px;
    font-size: 0.8rem;
    font-weight: 500;
    color: #6b6f70;
    line-height: 1.3;
`;

const LockedSizeNote = styled.p`
    margin: 0 0 10px;
    padding: 0 12px;
    font-size: 0.85em;
    color: #6b6f70;
`;

const FieldsetTitle = styled(Form.Label)`
    display: block;
    font-size: 1.1rem !important;
    font-weight: 600;
    margin-bottom: 0.8rem;
    color: ${({theme}) => theme.colors?.primary?.main ?? '#204380'};
`;

const RequiredAsterisk = styled.span`
    color: ${({theme}) => theme.colors?.errorLabel?.main ?? '#c92434'};
`;

const SizeSuffix = styled(InputGroup.Text)`
    background: transparent;
    border: 0;
    padding: 0 8px 2px;
    font-size: 0.85em;
    font-weight: 500;
    color: #6b6f70;
`;

const FooterButton = CBCButton as React.FC<{
    type?: string;
    onClick?: () => void;
    iconName?: string;
    className?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
    children?: React.ReactNode;
}>;

/* eslint-enable react/no-multi-comp -- colocated field helpers for this editor */
